/**
 * Users API
 * Unified user query via /users/complete endpoint
 */

import apiClient from './client';
import type {
  BetaTesterStats,
  UserStats,
  UserRoleLevel,
  UserCompleteView,
  UserCompleteListResponse,
  UserCompleteListParams,
} from '@/types/users';

export const usersApi = {
  // ============================================================================
  // User Query (via users_complete_view)
  // ============================================================================

  /**
   * List users with complete information from users_complete_view.
   * This includes profile, preference, and birth chart data in a single query.
   * Used by: UsersPage, UserSelector, EvalUserPicker, etc.
   */
  listUsersComplete: async (params: UserCompleteListParams = {}): Promise<UserCompleteListResponse> => {
    const response = await apiClient.get('/users/complete', { params });
    return response.data;
  },

  /**
   * Get complete user details by ID from users_complete_view.
   */
  getUserComplete: async (userId: string): Promise<UserCompleteView> => {
    const response = await apiClient.get(`/users/complete/${userId}`);
    return response.data;
  },

  // ============================================================================
  // Statistics & Admin Operations
  // ============================================================================

  /**
   * Get beta tester statistics
   */
  getBetaTesterStats: async (): Promise<BetaTesterStats> => {
    const response = await apiClient.get('/users/beta-testers/stats');
    return response.data;
  },

  /**
   * Update user's beta tester status
   */
  updateBetaTesterStatus: async (userId: string, isBetaTester: boolean): Promise<void> => {
    await apiClient.put(`/users/${userId}/beta-tester`, { is_beta_tester: isBetaTester });
  },

  /**
   * Batch update beta tester status
   */
  batchUpdateBetaTesters: async (userIds: string[], isBetaTester: boolean): Promise<{ updated_count: number }> => {
    const response = await apiClient.post('/users/beta-testers/batch', {
      user_ids: userIds,
      is_beta_tester: isBetaTester,
    });
    return response.data;
  },

  /**
   * Get comprehensive user statistics
   */
  getUserStats: async (): Promise<UserStats> => {
    const response = await apiClient.get('/users/stats');
    return response.data;
  },

  /**
   * Update user's admin status
   */
  updateAdminStatus: async (userId: string, isAdmin: boolean): Promise<void> => {
    await apiClient.put(`/users/${userId}/admin`, { is_admin: isAdmin });
  },

  /**
   * Update user's system role
   */
  updateSystemRole: async (userId: string, systemRole: string): Promise<void> => {
    await apiClient.put(`/users/${userId}/system-role`, { system_role: systemRole });
  },

  /**
   * Update user's role level
   */
  updateUserRole: async (userId: string, roleLevel: UserRoleLevel): Promise<void> => {
    await apiClient.put(`/users/${userId}/role`, { role_level: roleLevel });
  },

  /**
   * Update residence location on a user's astro profile (birth chart).
   */
  updateResidenceLocation: async (userId: string, profileId: string, residenceLocation: string): Promise<void> => {
    await apiClient.put(`/users/${userId}/birth-charts/${profileId}`, {
      residence_location: residenceLocation,
    });
  },

  /**
   * Manually override birth timezone (bypasses geocoding).
   */
  updateBirthTimezone: async (userId: string, profileId: string, timezone: string): Promise<void> => {
    await apiClient.put(`/users/${userId}/birth-charts/${profileId}`, {
      timezone,
    });
  },

  /**
   * Manually override residence timezone (bypasses geocoding).
   */
  updateResidenceTimezone: async (userId: string, profileId: string, timezone: string): Promise<void> => {
    await apiClient.put(`/users/${userId}/birth-charts/${profileId}`, {
      residence_timezone: timezone,
    });
  },
};
